import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGem, faSpinner } from '@/icons';
import { Modal } from './Modal';
import { useTheme } from '../contexts/ThemeContext';
import { ProDataReasonsNotice } from './ProDataReasonsNotice';
import type { NonSplitCustodyArrangement } from '../utils/proDataReasons';
import { useSingleFlight } from '../hooks/useSingleFlight';

interface ProDowngradeLockedModalProps {
    isOpen: boolean;
    /** The locked case. Its Pro-only data is listed, so the user sees why it is read-only. */
    caseId?: string | null;
    /** First names for the custody choices a split case asks for. */
    partyNames?: { partyA?: string | null; partyB?: string | null };
    /**
     * Copy this case: strips its Pro-only data and opens the editable copy. For a split case,
     * `custodyArrangement` is the non-split arrangement the user chose for the copy.
     * `requestId` is one per user action (see useSingleFlight); reject to keep it for a retry.
     */
    onMakeCopy: (custodyArrangement: NonSplitCustodyArrangement | undefined, requestId: string) => void | Promise<void>;
    /** Dismiss and stay on the (now read-only) case. */
    onContinueViewOnly: () => void;
    /** True while the copy request is in flight. */
    isCopying?: boolean;
}

/**
 * ProDowngrade: shown when a save is rejected because the team lost Pro while this tab had the
 * case open.
 *
 * Deliberately not dismissible by clicking outside or pressing Escape -- the user's edits can no
 * longer be saved to this case, so they need to make an explicit choice rather than keep typing
 * into a document that silently stops persisting. Their edits are preserved either way: the
 * rejected payload was backed up before the error surfaced.
 */
export const ProDowngradeLockedModal: React.FC<ProDowngradeLockedModalProps> = ({
    isOpen,
    caseId,
    partyNames,
    onMakeCopy,
    onContinueViewOnly,
    isCopying = false,
}) => {
    const theme = useTheme();
    const [custodyChoice, setCustodyChoice] = useState<NonSplitCustodyArrangement | null>(null);
    const [needsCustodyChoice, setNeedsCustodyChoice] = useState(false);
    // One copy at a time: blocks a double click or a repeated Enter from the first click until
    // the request settles. Each opening of the modal is a new user action.
    const copyFlight = useSingleFlight();
    const { resetRequestId } = copyFlight;
    useEffect(() => { setCustodyChoice(null); resetRequestId(); }, [caseId, isOpen, resetRequestId]);
    const copying = isCopying || copyFlight.inFlight;
    const copyBlocked = copying || (needsCustodyChoice && !custodyChoice);
    const handleMakeCopy = () => {
        if (copyBlocked) return;
        // The caller reports a failure; a rejection only keeps the request id for a retry.
        void copyFlight.run((requestId) => Promise.resolve(onMakeCopy(custodyChoice ?? undefined, requestId))).catch(() => {});
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => { /* not dismissible -- the user must choose */ }}
            title="Pro Case — Read Only"
            showCloseButton={false}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                <div style={{ display: 'flex', gap: theme.spacing.md, alignItems: 'flex-start' }}>
                    <FontAwesomeIcon
                        icon={faGem}
                        style={{ color: theme.colors.primary, fontSize: theme.typography.fontSize.xl, marginTop: '2px' }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                        <p style={{ margin: 0, color: theme.colors.text.primary }}>
                            This case uses Pro features and must be copied to make edits to it.
                        </p>
                        <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                            Your team's subscription changed while this case was open, so further
                            changes cannot be saved here. You can still download its documents.
                        </p>
                        <ProDataReasonsNotice
                            caseId={caseId}
                            enabled={isOpen}
                            intro="It is read-only because it holds this Pro-only data, which the copy will not include:"
                            custodyChoice={custodyChoice}
                            onCustodyChoiceChange={setCustodyChoice}
                            onNeedsCustodyChoiceChange={setNeedsCustodyChoice}
                            partyNames={partyNames}
                        />
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
                    <button
                        type="button"
                        onClick={onContinueViewOnly}
                        disabled={copying}
                        style={{
                            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                            borderRadius: theme.borderRadius.sm,
                            border: `1px solid ${theme.colors.border.default}`,
                            backgroundColor: theme.colors.surface,
                            color: theme.colors.text.primary,
                            cursor: copying ? 'not-allowed' : 'pointer',
                            opacity: copying ? 0.6 : 1,
                        }}
                    >
                        Continue in View-Only Mode
                    </button>
                    <button
                        type="button"
                        onClick={handleMakeCopy}
                        disabled={copyBlocked}
                        aria-busy={copying}
                        style={{
                            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                            borderRadius: theme.borderRadius.sm,
                            border: `1px solid ${theme.colors.primary}`,
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.text.inverse,
                            cursor: copyBlocked ? 'not-allowed' : 'pointer',
                            opacity: copyBlocked ? 0.6 : 1,
                        }}
                    >
                        {copying ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: theme.spacing.xs }} />
                                Copying…
                            </>
                        ) : 'Make a Copy'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};
